/**
 * Bengali (Bangabda) and Islamic (Hijri) calendar formatting for the age calculator.
 * Conversion lives in `utils/banglaCalendar` so this tool and the date-format tool
 * cannot drift apart.
 */

import { toBnDigits } from '../../../../../utils/bnFormat';
import { toBanglaDate, toHijriDate } from '../../../../../utils/banglaCalendar';

/**
 * @param {Date} date
 * @param {'en' | 'bn'} [locale]
 * @returns {string}
 */
export function formatBengaliCalendarDate(date, locale = 'bn') {
    const bangla = toBanglaDate(date);
    if (!bangla) return '';

    return locale === 'bn'
        ? `${toBnDigits(bangla.day)} ${bangla.monthBn}, ${toBnDigits(bangla.year)} বঙ্গাব্দ`
        : `${bangla.day} ${bangla.monthEn} ${bangla.year} BS`;
}

/**
 * Hijri / Islamic calendar in Arabic script.
 * @param {Date} date
 * @returns {string}
 */
export function formatArabicHijriDate(date) {
    if (!(date instanceof Date) || Number.isNaN(date.getTime())) return '';

    try {
        return new Intl.DateTimeFormat('ar-SA-u-ca-islamic-umalqura', {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
        }).format(date);
    } catch {
        const hijri = toHijriDate(date);
        return hijri ? `${hijri.day} ${hijri.monthAr} ${hijri.year} هـ` : '';
    }
}

/**
 * Hijri date with Bengali numerals (for BN UI). Built by hand rather than through
 * `Intl`, whose bn-BD era name renders as "যুগ" instead of "হিজরি".
 * @param {Date} date
 * @returns {string}
 */
export function formatBengaliHijriDate(date) {
    const hijri = toHijriDate(date);
    if (!hijri) return '';
    return `${toBnDigits(hijri.day)} ${hijri.monthBn}, ${toBnDigits(hijri.year)} হিজরি`;
}

/**
 * @param {Date} date
 * @param {'en' | 'bn'} locale
 * @returns {{ bengali: string, arabic: string, hijriBn: string }}
 */
export function getAlternateCalendarDates(date, locale = 'bn') {
    return {
        bengali: formatBengaliCalendarDate(date, locale),
        arabic: formatArabicHijriDate(date),
        hijriBn: formatBengaliHijriDate(date),
    };
}
